import analytics.TrendlineHelper
import analytics.PeriodSpending
import analytics.TrendPoint
import kotlinx.browser.document
import kotlinx.html.div
import kotlinx.html.dom.create
import kotlinx.html.id
import kotlinx.html.style
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLCanvasElement

/**
 * Web TrendChart component - Restored to use Chart.js with smoothed curves
 */
class TrendChart {
    private var chartInstance: Chart? = null
    private var currentContainerId: String = ""

    /**
     * Render chart using Chart.js
     */
    fun render(
        containerId: String,
        data: DoubleArray,
        dates: Array<String>? = null,
        currentPeriodIndex: Int? = null,
        timeInterval: String = "Monthly"
    ): HTMLElement {
        console.log(
            "🌐 WEB TREND CHART: render() called with containerId='$containerId', data size=${data.size}, timeInterval='$timeInterval'",
        )
        currentContainerId = containerId

        val container =
            document.getElementById(containerId) as? HTMLElement
                ?: document.body!!

        // Clear existing content
        container.innerHTML = ""

        // Create chart container
        val chartContainer =
            document.create.div {
                id = "${containerId}_chart_container"
                style = "width: 100%; height: 100%; min-height: 400px; position: relative;"
            }
        
        // Create canvas element manually to ensure it's properly typed/created
        val canvas = document.createElement("canvas") as HTMLCanvasElement
        canvas.id = "${containerId}_canvas"
        chartContainer.appendChild(canvas)

        container.appendChild(chartContainer)

        // Destroy existing chart if any
        chartInstance?.destroy()
        chartInstance = null

        val ctx = canvas.getContext("2d")
        
        // Prepare data for TrendlineHelper
        val periodSpendingList = data.mapIndexed { index, amount ->
            PeriodSpending(
                periodKey = dates?.getOrNull(index) ?: index.toString(),
                amount = amount
            )
        }

        // Calculate trendline and forecast
        var historicalTrend: List<TrendPoint> = emptyList()
        var forecastTrend: List<TrendPoint> = emptyList()
        var projectionLength = 0

        if (TrendlineHelper.hasSufficientData(data.toList())) {
            console.log("Calculating trendline...")
            val trendline = TrendlineHelper.calculateTrendline(periodSpendingList, timeInterval)
            if (trendline.isNotEmpty()) {
                val split = TrendlineHelper.splitTrendlineForChart(trendline)
                historicalTrend = split.first
                forecastTrend = split.second
                projectionLength = TrendlineHelper.getProjectionLength(timeInterval)
                console.log("Trendline calculated: ${historicalTrend.size} historical, ${forecastTrend.size} forecast points")
            }
        } else {
            console.log("Insufficient data for trendline")
        }

        // Prepare labels (historical + forecast)
        val baseLabels = dates?.toList() ?: List(data.size) { "Period ${it + 1}" }
        val forecastLabels = List(projectionLength) { "Future +${it + 1}" }
        val allLabels = (baseLabels + forecastLabels).toTypedArray()
        
        // Total points to render
        val totalPoints = data.size + projectionLength

        // Highlight current period if specified
        val pointBackgroundColors = Array(totalPoints) { index ->
            if (currentPeriodIndex != null && index == currentPeriodIndex) {
                "#28a745" // Green for selected period
            } else {
                "#667eea" // Purple for other periods
            }
        }

        val pointRadii = Array(totalPoints) { index ->
            if (currentPeriodIndex != null && index == currentPeriodIndex) {
                6
            } else {
                if (index < data.size) 4 else 0 // No radius for forecast points unless specified
            }
        }

        val chartConfig = js("{}").unsafeCast<ChartConfiguration>()
        chartConfig.type = "line"
        
        val chartData = js("{}").unsafeCast<ChartData>()
        chartData.labels = allLabels
        
        val datasetsList = mutableListOf<ChartDataset>()

        // 1. Actual Spending Dataset
        val spendingDataset = js("{}").unsafeCast<ChartDataset>()
        spendingDataset.label = "Spending"
        // Pad data with nulls for forecast periods
        val spendingData = Array<Double?>(totalPoints) { index ->
            if (index < data.size) data[index] else null
        }
        spendingDataset.data = spendingData.unsafeCast<Array<Any>>()
        spendingDataset.borderColor = "#667eea"
        spendingDataset.backgroundColor = "rgba(102, 126, 234, 0.2)" // Area fill
        spendingDataset.borderWidth = 3
        spendingDataset.tension = 0.4 // Smoothed curve
        spendingDataset.fill = true
        
        // Use dynamic access for array properties
        spendingDataset.asDynamic().pointBackgroundColor = pointBackgroundColors
        spendingDataset.asDynamic().pointRadius = pointRadii
        spendingDataset.asDynamic().pointHoverRadius = pointRadii.map { it + 2 }.toTypedArray()
        
        datasetsList.add(spendingDataset)

        // 2. Historical Trend Dataset (Dashed)
        if (historicalTrend.isNotEmpty()) {
            val trendDataset = js("{}").unsafeCast<ChartDataset>()
            trendDataset.label = "Trend"
            
            // Map trend points to chart data array
            val trendData = Array<Double?>(totalPoints) { null }
            
            // We need to account for leading zeros filtering in TrendlineHelper
            // TrendlineHelper filters leading zeros, so x=0 in trendline corresponds to first non-zero index in data
            val firstNonZeroIndex = data.indexOfFirst { it > 0.0 }
            val startIndex = if (firstNonZeroIndex >= 0) firstNonZeroIndex else 0
            
            historicalTrend.forEach { point ->
                val index = startIndex + point.x
                if (index < totalPoints) {
                    trendData[index] = point.y
                }
            }
            
            trendDataset.data = trendData.unsafeCast<Array<Any>>()
            trendDataset.borderColor = "#28a745" // Green
            trendDataset.borderWidth = 2
            trendDataset.asDynamic().borderDash = arrayOf(5, 5)
            trendDataset.asDynamic().pointRadius = 0 // Hide points on trend line
            trendDataset.fill = false
            trendDataset.tension = 0.4
            trendDataset.asDynamic().spanGaps = true // Important for connecting lines
            
            datasetsList.add(trendDataset)
        }

        // 3. Forecast Dataset (Dashed, distinct color)
        if (forecastTrend.isNotEmpty()) {
            val forecastDataset = js("{}").unsafeCast<ChartDataset>()
            forecastDataset.label = "Forecast"
            
            val forecastData = Array<Double?>(totalPoints) { null }
            val firstNonZeroIndex = data.indexOfFirst { it > 0.0 }
            val startIndex = if (firstNonZeroIndex >= 0) firstNonZeroIndex else 0
            
            forecastTrend.forEach { point ->
                val index = startIndex + point.x
                if (index < totalPoints) {
                    forecastData[index] = point.y
                }
            }
            
            forecastDataset.data = forecastData.unsafeCast<Array<Any>>()
            forecastDataset.borderColor = "#fd7e14" // Orange
            forecastDataset.borderWidth = 2
            forecastDataset.asDynamic().borderDash = arrayOf(5, 5)
            forecastDataset.asDynamic().pointRadius = 3
            forecastDataset.asDynamic().pointBackgroundColor = "#fd7e14"
            forecastDataset.fill = false
            forecastDataset.tension = 0.4
            
            datasetsList.add(forecastDataset)
        }

        chartData.datasets = datasetsList.toTypedArray()
        chartConfig.data = chartData
        
        val options = js("{}").unsafeCast<ChartOptions>()
        options.responsive = true
        options.maintainAspectRatio = false
        
        val plugins = js("{}").unsafeCast<PluginsOptions>()
        val legend = js("{}").unsafeCast<LegendOptions>()
        legend.display = true
        legend.position = "top"
        plugins.legend = legend
        
        val tooltip = js("{}").unsafeCast<TooltipOptions>()
        tooltip.enabled = true
        val tooltipCallbacks = js("{}")
        tooltipCallbacks.label = { context: dynamic ->
            val value = context.parsed.y
            val label = context.dataset.label
            "$label: $" + value.toFixed(2)
        }
        tooltip.callbacks = tooltipCallbacks
        plugins.tooltip = tooltip
        
        options.plugins = plugins
        
        val scales = js("{}")
        val yScale = js("{}")
        yScale.beginAtZero = true
        val yTicks = js("{}")
        yTicks.callback = { value: dynamic, _: dynamic, _: dynamic ->
            "$" + value.toString()
        }
        yScale.ticks = yTicks
        scales.y = yScale
        
        options.scales = scales
        
        chartConfig.options = options
        
        console.log("🌐 WEB TREND CHART: Creating Chart.js instance...")
        try {
            chartInstance = Chart(ctx, chartConfig)
            console.log("🌐 WEB TREND CHART: Chart created successfully")
        } catch (e: Exception) {
            console.error("🌐 WEB TREND CHART: Failed to create chart", e)
        }

        return chartContainer
    }
    
    // Helper methods for compatibility with WebComponents.kt
    
    fun scrollToDataPoint(index: Int) {
        // No-op for Chart.js as it fits to container
        console.log("🌐 WEB TREND CHART: scrollToDataPoint called (no-op for Chart.js)")
    }
    
    fun setSelectedMethod(methodId: String) {
        // No-op
        console.log("🌐 WEB TREND CHART: setSelectedMethod called (no-op)")
    }
    
    fun getSelectedMethodId(): String {
        return "smoothed"
    }
}
